import {
  PAYMENT_LINK_PERIODS,
  PAYMENT_LINK_STATUSES,
  SORT_DIRS,
  type PaymentLinkPeriod,
  type PaymentLinkStatus,
  type SortDir,
} from '@gozoki/api-types'
import { z } from 'zod'
import { DEFAULT_PAGE_SIZE, optionalCaught, pageSchema, pageSizeSchema } from '../customers-search'
import { paymentLinkStatusPresentation } from './payment-link-status'

/**
 * Filtres de l'écran Liens de paiement (`/users/payment-links`, kanban #870 lot 7), portés par le
 * `search` de la route : un lien partagé rouvre la même vue (recherche, statut, période, tri,
 * page). Pur et testé (`payment-links-search.test.mjs`). Statuts, périodes et sens de tri sont
 * ceux du contrat `GET /payment-links`, partagés avec le backend par `@gozoki/api-types`.
 */

export const DEFAULT_PAYMENT_LINKS_SORT_DIR: SortDir = 'desc'

export const paymentLinksSearchSchema = z.object({
  q: optionalCaught(z.string()),
  status: optionalCaught(z.enum(PAYMENT_LINK_STATUSES)),
  period: optionalCaught(z.literal(PAYMENT_LINK_PERIODS)),
  sortDir: optionalCaught(z.enum(SORT_DIRS)),
  page: pageSchema,
  pageSize: pageSizeSchema,
})

export type PaymentLinksSearch = z.infer<typeof paymentLinksSearchSchema>

export interface PaymentLinksListQuery {
  page: number
  pageSize: number
  search?: string
  status?: PaymentLinkStatus
  period?: PaymentLinkPeriod
  sortDir: SortDir
}

/** Paramètres de `GET /payment-links` : tous les liens, avec ou sans compte. */
export const searchToPaymentLinksQuery = (search: PaymentLinksSearch): PaymentLinksListQuery => {
  const q = search.q?.trim()
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? DEFAULT_PAGE_SIZE,
    ...(q ? { search: q } : {}),
    ...(search.status === undefined ? {} : { status: search.status }),
    ...(search.period === undefined ? {} : { period: search.period }),
    sortDir: search.sortDir ?? DEFAULT_PAYMENT_LINKS_SORT_DIR,
  }
}

/** Un changement de filtre ramène toujours à la première page. */
export const applyPaymentLinksFilter = (
  search: PaymentLinksSearch,
  change: Partial<PaymentLinksSearch>,
): PaymentLinksSearch => ({ ...search, ...change, page: undefined })

export const hasActivePaymentLinksFilters = (search: PaymentLinksSearch): boolean =>
  search.status !== undefined || search.period !== undefined || (search.q?.trim().length ?? 0) > 0

export const clearPaymentLinksFilters = (search: PaymentLinksSearch): PaymentLinksSearch => ({
  pageSize: search.pageSize,
  sortDir: search.sortDir,
})

export const PAYMENT_LINK_PERIOD_LABELS: Record<PaymentLinkPeriod, string> = {
  7: '7 jours',
  30: '30 jours',
  90: '90 jours',
  365: '1 an',
}

/** Options du filtre « Statut », dans l'ordre du cycle de vie du lien. */
export const PAYMENT_LINK_STATUS_FILTER_OPTIONS: { label: string; value: PaymentLinkStatus }[] =
  PAYMENT_LINK_STATUSES.map((status) => ({
    label: `Statut : ${paymentLinkStatusPresentation(status).label.toLowerCase()}`,
    value: status,
  }))

/** « tri : date de création décroissante » */
export const paymentLinksSortLabel = (sortDir: SortDir): string =>
  `tri : date de création ${sortDir === 'desc' ? 'décroissante' : 'croissante'}`
